import api from "./api";
import { BusinessService, ServiceStaffMember } from "../types/businessService";

interface ApiBusinessService {
  id: number;
  company_id: number;
  category_id: number | null;
  location_id: number | null;
  image?: string | string[] | null;
  name: string;
  slug?: string;
  description?: string | null;
  price?: number | string | null;
  time?: number | string | null;
  time_type?: string | null;
  discount?: number | string | null;
  discount_type?: string | null;
  net_price?: number | string | null;
  default_image?: string | null;
  status?: string | null;
  service_type?: string | null;
  created_at?: string;
  updated_at?: string;
  service_image_url?: string | null;
  service_detail_url?: string | null;
  converted_price?: number | string | null;
  converted_discounted_price?: number | string | null;
  formated_price?: string | null;
  discounted_price?: number | string | null;
  category?: {
    id: number;
    name: string;
    status?: string;
  } | null;
  location?: {
    id: number;
    name: string;
  } | null;
  users?: Array<{
    id?: number | string;
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  }>;
  enable_deposite?: string | null;
  deposite_type?: string | null;
  deposte_amount?: number | string | null;
  website_visibility?: "show" | "hide" | string | null;
  [key: string]: unknown;
}

interface ApiBusinessServicesResponse {
  success: boolean;
  data: ApiBusinessService[];
  message?: string;
}

const parseNumber = (value?: number | string | null): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const normalizeStatus = (
  status?: string | null
): "active" | "inactive" => {
  return status?.toLowerCase() === "inactive" ? "inactive" : "active";
};

const toMinutes = (time?: number | string | null, timeType?: string | null) => {
  const numericTime = parseNumber(time);
  const unit = (timeType || "minutes").toLowerCase();

  if (unit.startsWith("hour")) {
    return numericTime * 60;
  }
  if (unit.startsWith("day")) {
    return numericTime * 60 * 24;
  }
  return numericTime;
};

const formatDurationLabel = (minutes: number) => {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

const mapStaff = (users?: ApiBusinessService["users"]): ServiceStaffMember[] => {
  if (!Array.isArray(users)) return [];

  return users
    .map((user) => {
      if (!user) return null;
      const id = user.id ?? user.name ?? "";
      if (!id) return null;
      const name =
        user.name ||
        [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
        "Unnamed";
      return {
        id: String(id),
        name,
      };
    })
    .filter((member): member is ServiceStaffMember => Boolean(member));
};

export const mapApiServiceToBusinessService = (
  item: ApiBusinessService
): BusinessService => {
  const price =
    parseNumber(item.converted_price) ||
    parseNumber(item.price) ||
    parseNumber(item.net_price);

  const discountPrice =
    parseNumber(item.converted_discounted_price) ||
    parseNumber(item.discounted_price) ||
    parseNumber(item.net_price);

  const durationMinutes = toMinutes(item.time, item.time_type);
  const staff = mapStaff(item.users);
  const discountValue = parseNumber(item.discount);

  return {
    id: String(item.id),
    name: item.name || "Untitled service",
    slug: item.slug,
    description: item.description ?? null,
    price,
    discount: discountValue || undefined,
    discountPrice: discountPrice || undefined,
    discountType: item.discount_type || undefined,
    duration: durationMinutes || 0,
    durationLabel: formatDurationLabel(durationMinutes),
    timeUnit: item.time_type?.toLowerCase().startsWith("hour") ? "hours" : "minutes",
    categoryId: item.category_id ? String(item.category_id) : "",
    categoryName: item.category?.name || "Uncategorized",
    status: normalizeStatus(item.status),
    serviceType: item.service_type || "offline",
    staff,
    staffNames: staff.map((member) => member.name),
    taxes: Array.isArray(item.tax_services)
      ? item.tax_services
        .map((ts: any) => String(ts.tax_id || ts.tax?.id))
        .filter((id) => id && id !== "undefined")
      : item.service_taxes
        ? Array.isArray(item.service_taxes)
          ? item.service_taxes.map((st: any) =>
            String(st.tax_id || st.tax?.id || st.id || st)
          )
          : Object.values(item.service_taxes as Record<string, unknown>).map(
            String
          )
        : undefined,
    imageUrl:
      item.service_image_url ||
      item.default_image ||
      (Array.isArray(item.image) ? item.image[0] : item.image) ||
      null,
    serviceUrl: item.service_detail_url || null,
    locationName: item.location?.name || undefined,
    createdAt: item.created_at,
    raw: item,
    enableDeposit: (item.enable_deposite || "").toString().toLowerCase() === "yes",
    depositType:
      item.deposite_type === "percent" || item.deposite_type === "fixed"
        ? item.deposite_type
        : undefined,
    depositAmount: parseNumber(item.deposte_amount) || undefined,
    websiteVisibility:
      item.website_visibility === "hide" ? "hide" : "show",
  };
};

export const getBusinessServices = async (): Promise<BusinessService[]> => {
  const response = await api.get<ApiBusinessServicesResponse>(
    "/admin/business-services"
  );

  const payload = response.data;
  if (!payload?.success || !Array.isArray(payload.data)) {
    return [];
  }

  return payload.data.map(mapApiServiceToBusinessService);
};

export const getBusinessServiceById = async (id: string | number): Promise<BusinessService | null> => {
  const response = await api.get<ApiBusinessServiceResponse>(
    `/admin/business-services/${id}`
  );

  const payload = response.data;
  if (!payload?.success || !payload.data) {
    return null;
  }

  return mapApiServiceToBusinessService(payload.data);
};

export interface CreateBusinessServicePayload {
  name: string;
  slug: string;
  description?: string;
  price: number;
  time: number;
  time_type: "minutes" | "hours";
  category_id: number;
  location_id: number;
  service_type?: string;
  status?: "active" | "inactive";
  discount?: number;
  discount_type?: "percent" | "fixed";
  employee_ids?: number[];
  tax_ids?: number[];
  /** Backend: enable_deposite = 'yes' | 'no' */
  enable_deposite?: "yes" | "no";
  /** Backend: deposite_type = 'fixed' | 'percent' */
  deposite_type?: "fixed" | "percent";
  /** Backend: deposte_amount (typo in backend) */
  deposte_amount?: number;
  /** Backend: website_visibility = 'show' | 'hide' */
  website_visibility?: "show" | "hide";
}

interface ApiBusinessServiceResponse {
  success: boolean;
  data: ApiBusinessService;
  message?: string;
}

export const createBusinessService = async (
  payload: CreateBusinessServicePayload
): Promise<BusinessService> => {
  const response = await api.post<ApiBusinessServiceResponse>(
    "/admin/business-services",
    payload
  );

  const responsePayload = response.data;
  if (!responsePayload?.success || !responsePayload.data) {
    throw new Error("Failed to create service");
  }

  return mapApiServiceToBusinessService(responsePayload.data);
};

export const updateBusinessService = async (
  id: string,
  payload: CreateBusinessServicePayload
): Promise<BusinessService> => {
  const response = await api.put<ApiBusinessServiceResponse>(
    `/admin/business-services/${id}`,
    payload
  );

  const responsePayload = response.data;
  if (!responsePayload?.success || !responsePayload.data) {
    throw new Error("Failed to update service");
  }

  return mapApiServiceToBusinessService(responsePayload.data);
};

export const deleteBusinessService = async (id: string): Promise<void> => {
  const response = await api.delete(`/admin/business-services/${id}`);

  // Check if the response indicates success
  // Some APIs return success: true in the response body
  if (response.data && typeof response.data === 'object' && 'success' in response.data) {
    if (!response.data.success) {
      throw new Error("Failed to delete service");
    }
  }
  // If no response body or different format, assume success if status is 2xx
};

export interface ServiceImage {
  name: string;
  size: number;
  type: string;
  url: string;
}

export interface StoreImagesResponse {
  success: boolean;
  data: {
    service_id: number;
    images: string[];
    default_image: string | null;
  };
  message: string;
}

export const storeServiceImages = async (
  serviceId: string | number,
  imageFiles: File[],
  defaultImage?: string
): Promise<StoreImagesResponse> => {
  if (imageFiles.length === 0) {
    throw new Error("No image files provided");
  }

  const formData = new FormData();
  formData.append("service_id", String(serviceId));
  imageFiles.forEach((file) => {
    formData.append("file[]", file);
  });
  if (defaultImage) {
    formData.append("default_image", defaultImage);
  }

  const response = await api.post<StoreImagesResponse>(
    "/admin/business-services/store-images",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export interface UpdateImagesResponse {
  success: boolean;
  data: {
    service_id: number;
    images: string[];
    default_image: string | null;
  };
  message: string;
}

export const updateServiceImages = async (
  serviceId: string | number,
  newImageFiles: File[],
  existingImageFilenames: string[],
  defaultImage?: string
): Promise<UpdateImagesResponse> => {
  const formData = new FormData();
  formData.append("service_id", String(serviceId));

  // Add new image files
  if (newImageFiles.length > 0) {
    newImageFiles.forEach((file) => {
      formData.append("file[]", file);
    });
  }

  // Always send uploaded_files to preserve existing images
  formData.append("uploaded_files", JSON.stringify(existingImageFilenames));

  if (defaultImage) {
    formData.append("default_image", defaultImage);
  }

  const response = await api.post<UpdateImagesResponse>(
    "/admin/business-services/update-images",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export interface DeleteImageResponse {
  success: boolean;
  data: {
    service_id: number;
    images: string[];
    default_image: string | null;
  };
  message: string;
}

export const deleteServiceImage = async (
  serviceId: string | number,
  filename: string,
  defaultImage?: string
): Promise<DeleteImageResponse> => {
  const response = await api.post<DeleteImageResponse>(
    "/admin/business-services/delete-image",
    {
      serviceId: String(serviceId),
      file: filename,
      default_image: defaultImage || null,
    }
  );

  return response.data;
};

export interface Tax {
  id: number;
  name: string;
  percent: number;
  status: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateFormData {
  categories: Array<{
    id: number;
    company_id: number;
    name: string;
    slug: string;
    image: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    type: string;
    color_code: string | null;
    category_image_url: string;
  }>;
  locations: Array<{
    id: number;
    name: string;
    lat: number;
    lng: number;
    country_id: number;
    timezone_id: number;
    status: string;
    created_at: string;
    updated_at: string;
  }>;
  taxes: Tax[];
  employees: Array<{
    id: number;
    company_id: number;
    group_id: number | null;
    name: string;
    email: string;
    address: string | null;
    date_of_birth: string | null;
    calling_code: string | null;
    mobile: string | null;
    cus_source: string | null;
    landline: string | null;
    mobile_verified: number;
    image: string | null;
    rtl: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    country_id: number | null;
    slug: string | null;
    skin_type: string | null;
    user_image_url: string;
    mobile_with_code: string;
    formatted_mobile: string | null;
  }>;
  zoomStatus: string;
  package_modules: Record<string, string>;
}

export const getCreateFormData = async (): Promise<CreateFormData> => {
  const response = await api.get<{ success: boolean; data: CreateFormData; message?: string }>(
    "/admin/business-services/create"
  );

  const payload = response.data;
  if (!payload?.success || !payload.data) {
    throw new Error("Failed to fetch create form data");
  }

  return payload.data;
};

export interface EditFormData {
  service: ApiBusinessService;
  clean_slug: string;
  random_part: string;
  categories: Array<{
    id: number;
    company_id: number;
    name: string;
    slug: string;
    image: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    type: string;
    color_code: string | null;
    category_image_url: string;
  }>;
  locations: Array<{
    id: number;
    name: string;
    lat: number;
    lng: number;
    country_id: number;
    timezone_id: number;
    status: string;
    created_at: string;
    updated_at: string;
  }>;
  taxes: Tax[];
  employees: Array<{
    id: number;
    company_id: number;
    group_id: number | null;
    name: string;
    email: string;
    address: string | null;
    date_of_birth: string | null;
    calling_code: string | null;
    mobile: string | null;
    cus_source: string | null;
    landline: string | null;
    mobile_verified: number;
    image: string | null;
    rtl: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    country_id: number | null;
    slug: string | null;
    skin_type: string | null;
    user_image_url: string;
    mobile_with_code: string;
    formatted_mobile: string | null;
  }>;
  selectedUsers: number[];
  selectedTax: any[];
  images: ServiceImage[];
  package_modules: Record<string, string>;
  zoomStatus: string;
}

export const getEditFormData = async (id: string | number): Promise<EditFormData> => {
  const response = await api.get<{ success: boolean; data: EditFormData; message?: string }>(
    `/admin/business-services/${id}/edit`
  );

  const payload = response.data;
  if (!payload?.success || !payload.data) {
    throw new Error("Failed to fetch edit form data");
  }

  return payload.data;
};
